import { useTimezone } from '@app/core/hooks/useTimezone';
import { ReportView } from '@app/core/reports/components/ReportView';
import { zodResolver } from '@hookform/resolvers/zod';
import { webApi } from '@mi-front/api-clients';
import { useMemo } from 'react';
import { BaseLoggerReportsPage } from '../common/BaseLoggerReportsPage';
import streamQualityFixture from './fixtures/fixture.json';
import { mapStreamQualityValuesToRequest } from './form/map-stream-quality-values-to-request';
import { streamQualityReportFormSchema } from './form/stream-quality-report-schema';
import { StreamQualityReportForm } from './form/StreamQualityReportForm';
import { StreamQualityReportResult } from './form/StreamQualityReportResult';
import { StreamQualityReport } from './form/types';
import { useStreamQualityReportDefaultValues } from './form/use-stream-quality-report-default-values';
import { StreamQualityReportEmptyState } from './StreamQualityReportEmptyState';

export type StreamQualityReportPageProps = {
  /**
   * When set (e.g. JSON imported as `streamQualityFixture`), the result UI shows this data on load
   * without calling the API until the user runs the report again.
   */
  useFixture?: webApi.StreamQualityReportResponseDto;
};

/**
 * Stream quality report: filters in `ReportView` (RHF), result from POST `/stream-quality/report`.
 */
export function StreamQualityReportPage({
  useFixture:
    _useFixture = streamQualityFixture as unknown as webApi.StreamQualityReportResponseDto,
}: StreamQualityReportPageProps = {}) {
  const defaultValues = useStreamQualityReportDefaultValues();
  const tz = useTimezone();

  return (
    <BaseLoggerReportsPage>
      <ReportView<StreamQualityReport>
        formLibrary="react-hook-form"
        rhfFormProps={useMemo(
          () => ({
            resolver: zodResolver(streamQualityReportFormSchema),
          }),
          [],
        )}
        defaultValues={defaultValues}
        download={false}
        getResultCount={(result) => result.result.byStation.length}
        // initialResult={useFixture}
        reportFn={async (values) => {
          const body = mapStreamQualityValuesToRequest(values, tz);
          return webApi.postStreamQualityReport(body);
        }}
        runOnLoad
        emptyResult={<StreamQualityReportEmptyState />}
        Form={StreamQualityReportForm}
        Result={StreamQualityReportResult}
      />
    </BaseLoggerReportsPage>
  );
}
